import React, { useEffect, useRef } from 'react';
import { Briefcase, GraduationCap } from 'lucide-react';

interface TimelineItem {
  id: number;
  date: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  category: 'work' | 'education' | 'award';
}

const Timeline: React.FC = () => {
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('timeline-item-active');
          }
        });
      },
      {
        root: null,
        threshold: 0.2,
        rootMargin: '0px 0px -10% 0px',
      }
    );

    const timelineItems = document.querySelectorAll('.timeline-item');
    timelineItems.forEach((item) => {
      observer.observe(item);
    });

    return () => {
      timelineItems.forEach((item) => {
        observer.unobserve(item);
      });
    };
  }, []);

  const timelineItems: TimelineItem[] = [
    {
      id: 1,
      date: 'Nov 2024 - Feb 2025',
      title: 'Generative AI Intern',
      subtitle: 'Hidevs',
      description: 'Developed mock interview APIs and AI project idea generators using LangChain, RAG and Python. Built and optimized Llama-based AI pipelines for natural language processing tasks.',
      icon: <Briefcase className="h-5 w-5" />,
      category: 'work',
    },
    {
      id: 2,
      date: 'Mar 2023',
      title: 'Web Dev Intern',
      subtitle: 'BitstreamIo',
      description: 'Delivered a fully responsive corporate website using HTML, CSS, JavaScript, and Bootstrap, ensuring cross-browser compatibility and modern design standards with focus on UX and mobile-first accessibility.',
      icon: <Briefcase className="h-5 w-5" />,
      category: 'work',
    },
    {
      id: 3,
      date: '2024',
      title: 'Multiple Web Development Projects',
      subtitle: 'Freelance & Personal',
      description: 'Built 6+ production-ready web applications using React 18, TypeScript, and modern frameworks. Integrated with Supabase, MongoDB, and deployed on Vercel with focus on scalability and performance.',
      icon: <Briefcase className="h-5 w-5" />,
      category: 'work',
    },
    {
      id: 4,
      date: '2024',
      title: 'Hack the Web Hackathon',
      subtitle: 'KK Computers',
      description: 'Built a responsive frontend using React 18, TypeScript, Tailwind CSS, and shadcn/ui in 24 hours. Designed clean UI for courses, events, and role-based paths with smooth animations.',
      icon: <GraduationCap className="h-5 w-5" />,
      category: 'education',
    },
    {
      id: 5,
      date: '2023-2024',
      title: 'Web Development Certifications',
      subtitle: 'Various Platforms',
      description: 'Completed certifications in Introduction to Web Development, Database Management Systems, and Introduction to Data Mining to strengthen technical foundation.',
      icon: <GraduationCap className="h-5 w-5" />,
      category: 'education',
    }
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'work':
        return 'purple';
      case 'education':
        return 'blue';
      case 'award':
        return 'indigo';
      default:
        return 'purple';
    }
  };

  return (
    <section id="experience" className="py-24 bg-black relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-950/5 to-black"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold font-orbitron mb-4 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
            Experience & Education
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full mb-8"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-300">
            My professional journey through web development, from internships to building production-ready applications.
          </p>
        </div>

        <div ref={timelineRef} className="relative mx-auto max-w-3xl px-4">
          {/* Timeline Line */}
          <div className="absolute h-full w-0.5 bg-gradient-to-b from-purple-500 via-blue-500 to-indigo-500 left-6 md:left-1/2 md:-ml-0.5 top-0"></div>

          {/* Timeline Items */}
          <div className="space-y-12">
            {timelineItems.map((item, index) => {
              const isEven = index % 2 === 0;
              const categoryColor = getCategoryColor(item.category);

              return (
                <div
                  key={item.id}
                  className={`relative timeline-item opacity-0 transition-all duration-700 ease-out ${isEven ? 'md:translate-x-8' : 'md:-translate-x-8'
                    }`}
                >
                  <div
                    className={`flex items-center md:w-1/2 ${isEven ? 'md:ml-auto md:pl-8' : 'md:mr-auto md:pr-8'
                      }`}
                  >
                    {/* Timeline node */}
                    <div className="absolute flex items-center justify-center w-12 h-12 rounded-full border-2 border-purple-500 bg-gray-900 left-0 transform -translate-x-1/2 md:left-1/2 z-10">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full bg-${categoryColor}-600`}>
                        {item.icon}
                      </div>
                    </div>

                    {/* Content */}
                    <div
                      className={`relative w-full ml-12 md:ml-0 p-6 rounded-xl bg-gray-900/80 backdrop-blur-sm border border-${categoryColor}-900/30 hover:border-${categoryColor}-500/40 transition-colors duration-300 hover:shadow-${categoryColor}-500/10 hover:shadow-lg`}
                    >
                      <div className={`absolute h-0.5 w-8 bg-${categoryColor}-500 top-1/2 transform -translate-y-1/2 ${isEven ? 'md:-left-8' : 'left-0 md:hidden'
                        }`}></div>

                      <span className={`inline-block px-3 py-1 text-xs rounded-full bg-${categoryColor}-900/30 text-${categoryColor}-300 mb-3`}>
                        {item.date}
                      </span>
                      <h3 className="text-lg font-semibold font-orbitron text-white mb-1">
                        {item.title}
                      </h3>
                      <h4 className={`text-${categoryColor}-400 text-sm mb-3`}>
                        {item.subtitle}
                      </h4>
                      <p className="text-gray-400 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Timeline;